import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AppConfig,
  HomeView,
  MenuView,
  OfferView,
  OrderEventView,
  OrderHistoryItem,
  OrderView,
  PointHistoryItem,
  ProfileView,
  RewardCatalogItem,
  RewardWalletView,
  ScanTokenResponse,
  ServiceType,
  TierJourneyView,
} from '@ulp/consumer-core';
import { apiFetch, ApiError, configureClient } from './api';
import { cacheRead, cacheWrite } from './storage';
import { useTenant } from './tenant';
import { useSession } from './session';

/** Çevrimdışı-önce: ağ hatasında son kopya; başarıda kopyayı günceller. */
async function withCache<T>(slug: string, name: string, fetcher: () => Promise<T>): Promise<T> {
  try {
    const data = await fetcher();
    cacheWrite(slug, name, data);
    return data;
  } catch (e) {
    const cached = cacheRead<T>(slug, name);
    if (cached && e instanceof ApiError && e.isNetwork) return cached.data;
    throw e;
  }
}

function useKeys() {
  const { slug } = useTenant();
  const { memberId } = useSession();
  return { slug, memberId, k: (...parts: unknown[]) => [slug, memberId, ...parts] };
}

export function useAppConfig() {
  const { slug, k } = useKeys();
  const { token } = useSession();
  return useQuery({
    queryKey: k('config'),
    queryFn: () => withCache(slug, 'config', () => apiFetch<AppConfig>('/app/config')),
    staleTime: 5 * 60_000,
    enabled: !!token,
  });
}

export function useHome() {
  const { slug, k } = useKeys();
  const { signedIn } = useSession();
  return useQuery({
    queryKey: k('home'),
    queryFn: () => withCache(slug, 'home', () => apiFetch<HomeView>('/app/home')),
    enabled: signedIn,
    staleTime: 20_000,
  });
}

export function useOffers() {
  const { k } = useKeys();
  const { signedIn } = useSession();
  return useQuery({ queryKey: k('offers'), queryFn: () => apiFetch<OfferView[]>('/app/offers'), enabled: signedIn });
}

export function useProfile() {
  const { k } = useKeys();
  const { signedIn } = useSession();
  return useQuery({ queryKey: k('profile'), queryFn: () => apiFetch<ProfileView>('/app/me'), enabled: signedIn });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  const { k } = useKeys();
  return useMutation({
    mutationFn: (input: { fullName?: string; email?: string; phone?: string; marketingConsent?: boolean }) =>
      apiFetch<ProfileView>('/app/me', { method: 'PATCH', body: input }),
    onSuccess: (data) => {
      qc.setQueryData(k('profile'), data);
      void qc.invalidateQueries({ queryKey: k('home') });
    },
  });
}

export interface MenuQuery {
  branchId: string | null;
  serviceType: ServiceType | null;
}

export function useMenu(q: MenuQuery) {
  const { slug, k } = useKeys();
  const qc = useQueryClient();
  const { token } = useSession();
  useEffect(() => {
    configureClient({ onMenuVersion: () => void qc.invalidateQueries({ queryKey: [slug] , predicate: (x) => x.queryKey.includes('menu') }) });
    return () => configureClient({ onMenuVersion: null });
  }, [qc, slug]);
  const params = new URLSearchParams({ channel: 'MOBILE' });
  if (q.branchId) params.set('branchId', q.branchId);
  if (q.serviceType) params.set('serviceType', q.serviceType);
  const name = `menu-${q.branchId ?? ''}-${q.serviceType ?? ''}`;
  return useQuery({
    queryKey: k('menu', q.branchId ?? '', q.serviceType ?? ''),
    queryFn: () => withCache(slug, name, () => apiFetch<MenuView>(`/app/menu/view?${params.toString()}`)),
    staleTime: 60_000,
    enabled: !!token,
  });
}

export function useScanToken() {
  const { slug } = useTenant();
  return useMutation({
    mutationFn: async (input: { tipPercent?: number } = {}) => {
      const tip = input.tipPercent ?? 0;
      try {
        const t = await apiFetch<ScanTokenResponse>('/app/wallet/scan-token', { method: 'POST', body: { tipPercent: tip } });
        cacheWrite(slug, `scan-${tip}`, t);
        return t;
      } catch (e) {
        // Çevrimdışı: süresi dolmamış son karekod (kasa çevrimiçi doğrular).
        const cached = cacheRead<ScanTokenResponse>(slug, `scan-${tip}`);
        if (cached && new Date(cached.data.expiresAt).getTime() > Date.now() + 5_000) return cached.data;
        throw e;
      }
    },
  });
}

export interface PlaceOrderInput {
  clientRequestId: string;
  branchId?: string;
  note?: string;
  amount: string;
  fulfillmentType?: ServiceType;
  scheduledFor?: string;
  payFromWallet: boolean;
  items: Array<{ productId: string; quantity: number; optionIds?: string[]; note?: string }>;
}

export function usePlaceOrder() {
  const qc = useQueryClient();
  const { k } = useKeys();
  return useMutation({
    mutationFn: (input: PlaceOrderInput) =>
      apiFetch<OrderView>('/app/orders', {
        method: 'POST',
        body: { ...input, externalId: input.clientRequestId },
        headers: { 'idempotency-key': input.clientRequestId },
        timeoutMs: 20_000,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: k('home') });
      void qc.invalidateQueries({ queryKey: k('orders') });
      void qc.invalidateQueries({ queryKey: k('profile') });
    },
  });
}

const TERMINAL = ['COMPLETED', 'CANCELLED', 'REJECTED', 'REFUNDED', 'EXPIRED', 'DELIVERED'];

export function isTerminalOrder(o: Pick<OrderView, 'state' | 'fulfillmentStatus'> | undefined): boolean {
  const s = o?.state ?? o?.fulfillmentStatus ?? null;
  return !!s && TERMINAL.includes(s);
}

export function useOrder(orderId: string | undefined) {
  const { k } = useKeys();
  return useQuery({
    queryKey: k('order', orderId),
    queryFn: () => apiFetch<OrderView>(`/app/orders/${orderId}`),
    enabled: !!orderId,
    refetchInterval: (q) => (isTerminalOrder(q.state.data) ? false : 4000),
    refetchIntervalInBackground: false,
  });
}

export function useOrderEvents(orderId: string | undefined, active: boolean) {
  const { k } = useKeys();
  return useQuery({
    queryKey: k('order', orderId, 'events'),
    queryFn: () => apiFetch<OrderEventView[]>(`/app/orders/${orderId}/events`),
    enabled: !!orderId,
    refetchInterval: active ? 6000 : false,
  });
}

export function useCancelOrder() {
  const qc = useQueryClient();
  const { k } = useKeys();
  return useMutation({
    mutationFn: (input: { orderId: string; reason?: string }) =>
      apiFetch<OrderView>(`/app/orders/${input.orderId}/cancel`, { method: 'POST', body: { reason: input.reason } }),
    onSuccess: (data) => {
      qc.setQueryData(k('order', data.id), data);
      void qc.invalidateQueries({ queryKey: k('home') });
      void qc.invalidateQueries({ queryKey: k('orders') });
    },
  });
}

export function useOrderHistory() {
  const { k } = useKeys();
  const { token } = useSession();
  return useQuery({ queryKey: k('orders', 'history'), queryFn: () => apiFetch<OrderHistoryItem[]>('/app/orders'), enabled: !!token });
}

export function usePointHistory() {
  const { k } = useKeys();
  const { signedIn } = useSession();
  return useQuery({ queryKey: k('points'), queryFn: () => apiFetch<PointHistoryItem[]>('/app/points/history'), enabled: signedIn });
}

export function useRewardWallet() {
  const { k } = useKeys();
  const { signedIn } = useSession();
  return useQuery({ queryKey: k('rewards'), queryFn: () => apiFetch<RewardWalletView>('/app/rewards'), enabled: signedIn });
}

export function useRewardCatalog() {
  const { k } = useKeys();
  const { signedIn } = useSession();
  return useQuery({ queryKey: k('rewards', 'catalog'), queryFn: () => apiFetch<RewardCatalogItem[]>('/app/rewards/catalog'), enabled: signedIn, staleTime: 5 * 60_000 });
}

export function usePurchaseReward() {
  const qc = useQueryClient();
  const { k } = useKeys();
  return useMutation({
    mutationFn: (rewardId: string) => apiFetch('/app/rewards/purchase', { method: 'POST', body: { rewardId } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: k('rewards') });
      void qc.invalidateQueries({ queryKey: k('home') });
      void qc.invalidateQueries({ queryKey: k('points') });
    },
  });
}

export function useTierJourney() {
  const { k } = useKeys();
  const { signedIn } = useSession();
  return useQuery({ queryKey: k('tier'), queryFn: () => apiFetch<TierJourneyView>('/app/tier'), enabled: signedIn, staleTime: 5 * 60_000 });
}
